/* =============================================================================
   Wie groß, wie dick, wo die Mitte — gemessen an der eingestellten Handschrift

   Damit ein Bruchstrich oder ein Wurzelhaken so aussieht, als hätte ihn
   dieselbe Hand geschrieben wie die Buchstaben daneben, werden die Maße nicht
   geschätzt, sondern aus der Schrift selbst gelesen:

     xh       Höhe des kleinen x — die Grundeinheit aller Abstände
     achse    Mitte von + und − — darauf liegt der Bruchstrich
     dicke    Strichstärke — vom Bindestrich der Schrift abgenommen
     r        wo die Grundlinie in einem Zeichen-Element liegt (wie in
              schriftMetrik.js, nur für absolut gesetzte Zeichen)

   Alle Werte gelten für 1 px Schriftgröße und werden beim Setzen
   multipliziert. Gemerkt wird erst, wenn die Schrift wirklich geladen ist —
   sonst würde die Ersatzschrift des Browsers vermessen.
   ========================================================================== */

const merker = new Map();
const PROBE = 100;

let leinwand = null;
function ctx2d() {
  if (!leinwand) leinwand = document.createElement("canvas");
  return leinwand.getContext("2d", { willReadFrequently: true });
}

function familien(stapel) {
  return String(stapel)
    .split(",")
    .map((f) => f.trim())
    .filter(Boolean);
}

function schriftGeladen(stapel) {
  try {
    if (!document.fonts || typeof document.fonts.check !== "function") return true;
    return familien(stapel).every((f) => document.fonts.check(`${PROBE}px ${f}`));
  } catch {
    return true;
  }
}

/* Dicke eines liegenden Strichs: Spalte in der Mitte der Tinte abzählen. */
function messeStrichDicke(ctx, stapel) {
  const groesse = 200;
  const w = 300;
  const h = 300;
  if (ctx.canvas.width < w) ctx.canvas.width = w;
  if (ctx.canvas.height < h) ctx.canvas.height = h;
  ctx.clearRect(0, 0, w, h);
  ctx.font = `${groesse}px ${stapel}`;
  ctx.fillStyle = "#000";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("-", 40, 220);
  const daten = ctx.getImageData(0, 0, w, h).data;
  let minX = w;
  let maxX = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (daten[(y * w + x) * 4 + 3] > 128) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
      }
    }
  }
  if (maxX < 0) return 0.065;
  const dicken = [];
  for (const anteil of [0.35, 0.5, 0.65]) {
    const x = Math.round(minX + (maxX - minX) * anteil);
    let n = 0;
    for (let y = 0; y < h; y++) if (daten[(y * w + x) * 4 + 3] > 128) n++;
    if (n) dicken.push(n);
  }
  if (!dicken.length) return 0.065;
  dicken.sort((a, b) => a - b);
  const d = dicken[Math.floor(dicken.length / 2)] / groesse;
  return Math.min(0.12, Math.max(0.025, d));
}

/* Dicke eines stehenden Strichs: der Schaft des „i“ der Hauptschrift, quer
   abgezählt in der Mitte der x-Höhe. Der Bindestrich kommt oft aus der
   Ersatzschrift und ist dicker als die eigentliche Handschrift. */
function messeSchaftDicke(ctx, schrift) {
  const groesse = 200;
  const w = 300;
  const h = 300;
  ctx.clearRect(0, 0, w, h);
  ctx.font = `${groesse}px ${schrift}`;
  ctx.fillStyle = "#000";
  ctx.textBaseline = "alphabetic";
  const m = ctx.measureText("i");
  const xh = ctx.measureText("x").actualBoundingBoxAscent || 0;
  if (!(xh > 5)) return null;
  const basis = 230;
  ctx.fillText("i", 60, basis);
  const daten = ctx.getImageData(0, 0, w, h).data;
  const laeufe = [];
  for (let y = Math.round(basis - xh * 0.65); y <= Math.round(basis - xh * 0.3); y++) {
    let lauf = 0;
    let kuerzester = Infinity;
    for (let x = 0; x < w; x++) {
      if (daten[(y * w + x) * 4 + 3] > 128) lauf++;
      else if (lauf) {
        kuerzester = Math.min(kuerzester, lauf);
        lauf = 0;
      }
    }
    if (lauf) kuerzester = Math.min(kuerzester, lauf);
    if (isFinite(kuerzester)) laeufe.push(kuerzester);
  }
  if (!laeufe.length || !m.width) return null;
  laeufe.sort((a, b) => a - b);
  // Der Schaft steht meist leicht schräg: quer gemessen ist er etwas breiter
  const d = (laeufe[Math.floor(laeufe.length / 2)] * 0.92) / groesse;
  return Math.min(0.12, Math.max(0.02, d));
}

/* Wo liegt die Grundlinie in einem absolut gesetzten Zeichen mit
   line-height 0 — gemessen vom oberen Rand des Elements, je px Schriftgröße. */
function messeGrundlinienAnteil(stapel) {
  const kasten = document.createElement("div");
  kasten.style.cssText =
    `position:absolute;left:-9999px;top:0;visibility:hidden;` +
    `font-family:${stapel};font-size:${PROBE}px;line-height:35px;`;
  kasten.innerHTML =
    `<span style="position:relative;display:inline-block;width:0;height:0;">` +
    `<span class="z" style="position:absolute;left:0;top:0;line-height:0;font-size:${PROBE}px;white-space:pre;">H` +
    `<span class="p" style="display:inline-block;width:0;height:0;vertical-align:baseline;"></span></span></span>`;
  document.body.appendChild(kasten);
  const oben = kasten.querySelector(".z").getBoundingClientRect().top;
  const basis = kasten.querySelector(".p").getBoundingClientRect().top;
  kasten.remove();
  const v = (basis - oben) / PROBE;
  return isFinite(v) ? v : 0;
}

/* Hat irgendeine Schrift im Stapel dieses Zeichen? Fehlt es überall, nimmt
   der Browser eine Systemschrift — und die ist je nach Ersatz verschieden
   breit. Gleiche Breite mit zwei verschiedenen Ersatzschriften heißt also:
   das Zeichen kommt aus dem Stapel. */
function hatZeichen(ctx, stapel, ch) {
  ctx.font = `${PROBE}px ${stapel}, monospace`;
  const a = ctx.measureText(ch).width;
  ctx.font = `${PROBE}px ${stapel}, serif`;
  const b = ctx.measureText(ch).width;
  if (Math.abs(a - b) > 0.01) return false;
  ctx.font = `${PROBE}px ${stapel}, sans-serif`;
  const c = ctx.measureText(ch).width;
  return Math.abs(a - c) <= 0.01 && a > 0;
}

export function matheMetrik(stapel) {
  if (merker.has(stapel)) return merker.get(stapel);
  const ctx = ctx2d();
  const geladen = schriftGeladen(stapel);
  const hauptschrift = familien(stapel)[0] || stapel;

  const glyphMerker = new Map();
  const hatMerker = new Map();
  const hauptMerker = new Map();

  const hat = (ch) => {
    if (hatMerker.has(ch)) return hatMerker.get(ch);
    const v = hatZeichen(ctx, stapel, ch);
    hatMerker.set(ch, v);
    return v;
  };
  const inHauptschrift = (ch) => {
    if (hauptMerker.has(ch)) return hauptMerker.get(ch);
    const v = hatZeichen(ctx, hauptschrift, ch);
    hauptMerker.set(ch, v);
    return v;
  };

  const roh = (ch) => {
    ctx.font = `${PROBE}px ${stapel}`;
    const m = ctx.measureText(ch);
    return {
      breite: m.width / PROBE,
      oben: (m.actualBoundingBoxAscent || 0) / PROBE,
      unten: (m.actualBoundingBoxDescent || 0) / PROBE,
      links: -(m.actualBoundingBoxLeft || 0) / PROBE,
      rechts: (m.actualBoundingBoxRight || m.width) / PROBE,
    };
  };

  /* Viele Schriften haben keine Ziffern und kein + = ( ) — die kommen aus der
     Ersatzschrift. Sitzen deren Ziffern über der Grundlinie, obwohl die
     Buchstaben der Hauptschrift darauf stehen, wirkt 2x wie ²x. Dann wird
     alles aus der Ersatzschrift um genau diesen Betrag abgesenkt. Passen
     beide Schriften zusammen, ergibt die Messung 0 und es ändert sich nichts. */
  const xRoh = roh("x");
  let ersatzVersatz = 0;
  if (!inHauptschrift("2") && hat("2")) {
    const boeden = "0123456789"
      .split("")
      .map((z) => -roh(z).unten)
      .sort((a, b) => a - b);
    const boden = boeden[Math.floor(boeden.length / 2)];
    const xBoden = -xRoh.unten;
    if (boden - xBoden > Math.max(0.02, xRoh.oben * 0.25)) ersatzVersatz = boden - xBoden;
  }

  /* Wie weit ein Zeichen abgesenkt wird. Ziffern der Ersatzschrift einzeln
     auf die Linie (ihre Unterkanten springen dort zwischen 0,10 und 0,16
     Geviert), alles andere aus der Ersatzschrift um den mittleren Betrag. */
  const absenkung = (ch) => {
    if (!ersatzVersatz || inHauptschrift(ch)) return 0;
    if (/[0-9]/.test(ch)) return Math.max(0, -roh(ch).unten);
    return ersatzVersatz;
  };

  const glyph = (ch) => {
    if (glyphMerker.has(ch)) return glyphMerker.get(ch);
    const m = roh(ch);
    const v = absenkung(ch);
    const wert = { ...m, oben: m.oben - v, unten: m.unten + v, versatz: v };
    glyphMerker.set(ch, wert);
    return wert;
  };

  const x = glyph("x");
  const xh = x.oben > 0.05 ? x.oben : 0.27;
  const minus = glyph("-");
  const plus = glyph("+");
  const mitte = (gl) => (gl.oben - gl.unten) / 2;
  let achse = hat("-") ? (mitte(minus) + (hat("+") ? mitte(plus) : mitte(minus))) / 2 : xh * 0.5;
  if (!(achse > xh * 0.2 && achse < xh * 1.2)) achse = xh * 0.5;
  const ziffer = glyph("4");
  const ober = Math.max(glyph("H").oben, ziffer.oben, xh * 1.4);
  const unterlaenge = Math.max(glyph("g").unten, xh * 0.4);

  const metrik = {
    stapel,
    geladen,
    xh,
    achse,
    ziffernHoehe: ziffer.oben > xh ? ziffer.oben : xh * 1.6,
    oberlaenge: ober,
    unterlaenge,
    dicke: (() => {
      const quer = messeStrichDicke(ctx, stapel);
      const schaft = inHauptschrift("i") ? messeSchaftDicke(ctx, hauptschrift) : null;
      return schaft ? Math.min(quer, schaft) : quer;
    })(),
    leer: glyph(" ").breite || 0.25,
    r: messeGrundlinienAnteil(stapel),
    glyph,
    hat,
    // für den normalen Text in Home.js: dieselbe Absenkung der Ersatzschrift
    ersatzVersatz,
    absenkung,
  };

  if (geladen) merker.set(stapel, metrik);
  return metrik;
}

export function leereMatheMetrik() {
  merker.clear();
}
